import { FC, useCallback, useContext, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";

import Dialog from "../../../../components/dialog/Dialog";
import useResetUserPassword from "../../../../apis/auth/useResetUserPassword";
import { NotificationContext } from "../../../../context/NotificationContext";
import { IFirmUser } from "../../../../interfaces";
import { PASSWORD_MAX_LENGTH } from "../../../../constants";
import {
  enforceMaxLength,
  getMaxLengthAttemptHandlers,
  getMaxLengthExceededMessage,
  useMaxLengthExceeded,
} from "../../../../utills";

interface INewPassword {
  isOpen: boolean;
  user: IFirmUser;
  onClose: () => void;
}

const NewPassword: FC<INewPassword> = ({ isOpen, user, onClose }) => {
  const resetUserPassword = useResetUserPassword();
  const { createNotification } = useContext(NotificationContext);

  // PR 12234 Start
  const [newPasswordLimitExceeded, setNewPasswordLimitExceeded] =
    useMaxLengthExceeded();
  const [confirmNewPasswordLimitExceeded, setConfirmNewPasswordLimitExceeded] =
    useMaxLengthExceeded();
  // PR 12234 End

  const { control, handleSubmit } = useForm({
    resolver: yupResolver(
      yup
        .object({
          newPassword: yup
            .string()
            .trim()
            .required("Error: New Password is required."),
          confirmNewPassword: yup
            .string()
            .trim()
            .required("Error: New Password is required.")
            .oneOf([yup.ref("newPassword")], "Password does not match!"),
        })
        .required()
    ),
  });

  const onSubmit = useCallback(
    async (data: { newPassword: string }) => {
      resetUserPassword.mutate({
        email: user.email,
        userId: user.userID,
        password: data.newPassword,
      });
    },
    [resetUserPassword, user]
  );

  useEffect(() => {
    if (resetUserPassword.isSuccess) {
      createNotification({
        title: "Change Password Notification",
        type: "success",
        message: "Password changed successfully",
      });

      onClose();
    }
    // eslint-disable-next-line
  }, [resetUserPassword.isSuccess, createNotification]);

  useEffect(() => {
    if (resetUserPassword.isError) {
      createNotification({
        title: "Change Password Notification",
        type: "error",
        message:
          resetUserPassword.error.response?.data?.error?.errorText ||
          "Something went wrong while updating password",
      });
    }
    // eslint-disable-next-line
  }, [resetUserPassword.isError, createNotification]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Enter New Password"
      maxWidth="sm"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name="newPassword"
              control={control}
              render={({ field: { ref, ...field }, fieldState }) => (
                <TextField
                  className="w-100"
                  id={field.name}
                  InputLabelProps={{
                    required: true,
                  }}
                  // PR 12234 Start
                  inputProps={{
                    maxLength: PASSWORD_MAX_LENGTH,
                    ...getMaxLengthAttemptHandlers(
                      field.value ?? "",
                      PASSWORD_MAX_LENGTH,
                      setNewPasswordLimitExceeded
                    ),
                  }}
                  // PR 12234 End
                  inputRef={ref}
                  {...field}
                  // PR 12234 Start
                  onChange={(e) =>
                    enforceMaxLength(
                      e.target.value,
                      PASSWORD_MAX_LENGTH,
                      field.onChange,
                      setNewPasswordLimitExceeded
                    )
                  }
                  // PR 12234 End
                  onBlur={() => {
                    field.onChange(field.value?.trim());
                    field.onBlur();
                  }}
                  label="New Password"
                  placeholder="************"
                  type="password"
                  variant="standard"
                  error={!!fieldState.error}
                  // PR 12234 Start
                  helperText={
                    fieldState.error ? (
                      fieldState.error.message
                    ) : newPasswordLimitExceeded ? (
                      <Box component="span" sx={{ color: "warning.main" }}>
                        {getMaxLengthExceededMessage("New Password")}
                      </Box>
                    ) : null
                  }
                  // PR 12234 End
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="confirmNewPassword"
              control={control}
              render={({ field: { ref, ...field }, fieldState }) => (
                <TextField
                  className="w-100"
                  id={field.name}
                  InputLabelProps={{
                    required: true,
                  }}
                  // PR 12234 Start
                  inputProps={{
                    maxLength: PASSWORD_MAX_LENGTH,
                    ...getMaxLengthAttemptHandlers(
                      field.value ?? "",
                      PASSWORD_MAX_LENGTH,
                      setConfirmNewPasswordLimitExceeded
                    ),
                  }}
                  // PR 12234 End
                  inputRef={ref}
                  {...field}
                  // PR 12234 Start
                  onChange={(e) =>
                    enforceMaxLength(
                      e.target.value,
                      PASSWORD_MAX_LENGTH,
                      field.onChange,
                      setConfirmNewPasswordLimitExceeded
                    )
                  }
                  // PR 12234 End
                  onBlur={() => {
                    field.onChange(field.value?.trim());
                    field.onBlur();
                  }}
                  label="Re-enter New Password"
                  placeholder="************"
                  type="password"
                  variant="standard"
                  error={!!fieldState.error}
                  // PR 12234 Start
                  helperText={
                    fieldState.error ? (
                      fieldState.error.message
                    ) : confirmNewPasswordLimitExceeded ? (
                      <Box component="span" sx={{ color: "warning.main" }}>
                        {getMaxLengthExceededMessage("Re-enter New Password")}
                      </Box>
                    ) : null
                  }
                  // PR 12234 End
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Box display={"flex"} justifyContent={"end"}>
              <Button
                type="button"
                variant="contained"
                className="btn button-outline-secondary"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                className="btn primary-button ms-2"
                disabled={resetUserPassword.isLoading}
              >
                {resetUserPassword.isLoading ? (
                  <CircularProgress
                    sx={{
                      height: "25px !important",
                      width: "25px !important",
                      "--CircularProgress-size": "8px",
                      "--CircularProgress-trackThickness": "1px",
                      "--CircularProgress-progressThickness": "1px",
                    }}
                    color="inherit"
                  />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Dialog>
  );
};

export default NewPassword;
